// EP-160 T160-104x (AC-12): blackout for ID-card copies, done in the browser
// BEFORE the upload so the stored file never holds the religion or blood-group
// fields. The pixel work is pure (blackoutPixels, scaleBoxes) and tested on a
// real decoded buffer; the canvas glue at the bottom stays thin.

export interface Box {
  x: number
  y: number
  width: number
  height: number
}

export interface Size {
  width: number
  height: number
}

export const BLACKOUT_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const

export function isBlackoutType(type: string): boolean {
  return (BLACKOUT_MIME_TYPES as readonly string[]).includes(type)
}

/**
 * Two starting boxes over the religion and blood-group lines of a Thai ID
 * card (back-side layout), as fractions of the image. They are a starting
 * point only: the operator drags and resizes them over the real fields.
 */
export function defaultIdCardBoxes({ width, height }: Size): Box[] {
  return [
    { x: 0.5 * width, y: 0.36 * height, width: 0.3 * width, height: 0.1 * height },
    { x: 0.5 * width, y: 0.5 * height, width: 0.2 * width, height: 0.1 * height },
  ]
}

/** Maps boxes measured on a `from` surface (the screen) onto a `to` surface (image pixels). */
export function scaleBoxes(boxes: Box[], from: Size, to: Size): Box[] {
  if (from.width <= 0 || from.height <= 0) return boxes.map((b) => ({ ...b }))
  const sx = to.width / from.width
  const sy = to.height / from.height
  return boxes.map((b) => ({ x: b.x * sx, y: b.y * sy, width: b.width * sx, height: b.height * sy }))
}

/**
 * Paints the boxes solid black on an RGBA buffer, in place, and returns it.
 * Boxes are rounded outward to whole pixels and clipped to the image; a box
 * entirely outside the image paints nothing.
 */
export function blackoutPixels(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  boxes: Box[],
): Uint8ClampedArray {
  for (const box of boxes) {
    const x0 = Math.max(0, Math.floor(box.x))
    const y0 = Math.max(0, Math.floor(box.y))
    const x1 = Math.min(width, Math.ceil(box.x + box.width))
    const y1 = Math.min(height, Math.ceil(box.y + box.height))
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const i = (y * width + x) * 4
        data[i] = 0
        data[i + 1] = 0
        data[i + 2] = 0
        data[i + 3] = 255
      }
    }
  }
  return data
}

// ---------------------------------------------------------------------------
// Canvas glue
// ---------------------------------------------------------------------------

/** Decodes the file (EXIF orientation applied by the browser) to read its pixel size. */
export async function readImageSize(file: Blob): Promise<Size> {
  const bitmap = await createImageBitmap(file)
  const size = { width: bitmap.width, height: bitmap.height }
  bitmap.close()
  return size
}

/** Returns a new JPEG whose `boxes` (in image pixels) are filled solid black. */
export async function blackoutImageToJpeg(file: Blob, boxes: Box[]): Promise<Blob> {
  const bitmap = await createImageBitmap(file)
  const canvas = document.createElement('canvas')
  canvas.width = bitmap.width
  canvas.height = bitmap.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    bitmap.close()
    throw new Error('canvas_unavailable')
  }
  // JPEG has no alpha: paint white first so a transparent PNG does not turn black.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height)
  blackoutPixels(image.data, canvas.width, canvas.height, boxes)
  ctx.putImageData(image, 0, 0)
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas_export_failed'))),
      'image/jpeg',
      0.92,
    )
  })
}
